import { Pause, Play, RotateCw } from "lucide-react";

import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import {
  AUTO_REFRESH_LABELS,
  getAutoRefreshCountdownLabel,
} from "@/constants/auto-refresh.constants";
import { isDefined } from "@/lib/common";
import { cn } from "@/lib/utils";
import { NEUTRAL_TEXT_COLORS } from "@/themes";

import { AutoRefreshRing } from "./auto-refresh-ring";
import { useAutoRefreshState } from "./auto-refresh-provider";

const getStatusLabel = (isRefreshing: boolean, isPaused: boolean, isSuspended: boolean, secondsRemaining: number) => {
  if (isRefreshing) return AUTO_REFRESH_LABELS.REFRESHING;
  if (isPaused || isSuspended) return AUTO_REFRESH_LABELS.PAUSED;
  return getAutoRefreshCountdownLabel(secondsRemaining);
};

/**
 * The auto-refresh pill: countdown ring + status text, a pause/resume toggle, and a manual
 * refresh-now button. Lives in the app shell's fixed top-right slot and renders
 * nothing until the current page opts in via `useRegisterAutoRefresh`.
 */
export const AutoRefreshIndicator = ({ className }: { className?: string }) => {
  const autoRefresh = useAutoRefreshState();

  if (!isDefined(autoRefresh)) return null;

  const { secondsRemaining, progress, isRefreshing, isPaused, isSuspended, togglePause, refreshNow } = autoRefresh;

  const statusLabel = getStatusLabel(isRefreshing, isPaused, isSuspended, secondsRemaining);
  // Only state changes are announced. The seconds tick every second, and a live region on them
  // would make a screen reader read the pill aloud once per second for as long as the page is open.
  const announcedLabel = isRefreshing
    ? AUTO_REFRESH_LABELS.REFRESHING
    : isPaused || isSuspended
      ? AUTO_REFRESH_LABELS.PAUSED
      : AUTO_REFRESH_LABELS.ACTIVE;
  const pauseToggleLabel = isPaused ? AUTO_REFRESH_LABELS.RESUME_BUTTON_LABEL : AUTO_REFRESH_LABELS.PAUSE_BUTTON_LABEL;

  return (
    <Box
      className={cn(
        "flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 shadow-sm",
        className,
      )}
    >
      <AutoRefreshRing progress={progress} isRefreshing={isRefreshing} />

      <Typography
        variant="small"
        weight="medium"
        color={NEUTRAL_TEXT_COLORS[600]}
        className="min-w-0 whitespace-nowrap"
        aria-hidden
      >
        {statusLabel}
      </Typography>

      <span className="sr-only" aria-live="polite">
        {announcedLabel}
      </span>

      <span aria-hidden className="mx-0.5 h-3.5 w-px shrink-0 bg-neutral-200" />

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            aria-label={pauseToggleLabel}
            aria-pressed={isPaused}
            onClick={togglePause}
          >
            {/* Solid glyphs: lucide's stroked Pause reads as two vague blobs at this size. */}
            {isPaused ? (
              <Play className="h-3 w-3 fill-current" strokeWidth={0} />
            ) : (
              <Pause className="h-3 w-3 fill-current" strokeWidth={0} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{pauseToggleLabel}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            aria-label={AUTO_REFRESH_LABELS.REFRESH_NOW_BUTTON_LABEL}
            disabled={isRefreshing}
            onClick={refreshNow}
          >
            <RotateCw className="h-3.5 w-3.5" strokeWidth={2.25} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{AUTO_REFRESH_LABELS.REFRESH_NOW_BUTTON_LABEL}</TooltipContent>
      </Tooltip>
    </Box>
  );
};
